import { useState, useMemo } from 'react';
import { B, font, space, radius, fonts, weight } from '../constants/brand';
import { fmt } from '../utils/format';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Btn, Spinner, ErrorMsg, Card, Modal, PageHeader, Icon, Avatar, Badge, clickable } from '../components/ui';
import { NaicsEditModal } from '../components/shared/NaicsEditModal';

// Statuses worth calling out inline next to a task. "Not Started" and "Complete"
// are intentionally omitted.
const INLINE_STATUSES = ["In Progress", "On Hold", "In Review"];

// Due date shown as mm/dd/yy. Parse ISO date-only strings directly to avoid
// timezone drift (a UTC-midnight date can otherwise display as the prior day).
const fmtMDY = (d) => {
  if (!d) return "";
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[2]}/${m[3]}/${m[1].slice(-2)}`;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return `${String(dt.getMonth() + 1).padStart(2, "0")}/${String(dt.getDate()).padStart(2, "0")}/${String(dt.getFullYear()).slice(-2)}`;
};

// Notes are stored either as BlockNote JSON (an array of blocks) or legacy HTML.
// Render a clean one-line preview for either — never the raw JSON.
const blocksToText = (blocks) => {
  const out = [];
  const walk = (arr) => {
    for (const b of arr || []) {
      if (Array.isArray(b?.content)) out.push(b.content.map(c => (typeof c === "string" ? c : (c?.text || ""))).join(""));
      else if (typeof b?.content === "string") out.push(b.content);
      if (Array.isArray(b?.children) && b.children.length) walk(b.children);
    }
  };
  walk(blocks);
  return out.join(" ").replace(/\s+/g, " ").trim();
};
const noteToText = (s) => {
  if (!s) return "";
  const str = String(s).trim();
  if (str.startsWith("[") && str.endsWith("]")) {
    try { const t = blocksToText(JSON.parse(str)); if (t) return t; } catch { /* fall through */ }
  }
  return str.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
};

// Avatars for the "Person in Charge" column (deal-level Harvus leads).
const LeadAvatars = ({ names }) => {
  const list = (names || "").split(", ").map(n => n.trim()).filter(Boolean);
  if (list.length === 0) return <span style={{ fontSize: font.xs, color: B.textLt, fontStyle: "italic" }}>Unassigned</span>;
  return list.map(name => (
    <div key={name} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
      <Avatar name={name} initials={fmt.initials(name)} size={20} />
      <span style={{ fontSize: font.xs, color: B.text }}>{name}</span>
    </div>
  ));
};

// Latest Update popup body — lazy-loads the full notes + tasks history for the
// deal (or a specific deal detail), merged and sorted by date, most recent first.
const HistoryBody = ({ dealId, dealDetailId }) => {
  const { data, loading, error } = useApi(async () => {
    // includeDealLevel=true returns every note under the deal (deal-level + all
    // details) when no dealDetailId is given, matching the deal-level rollup so
    // the history isn't empty when the latest update lives on a detail.
    const notesUrl = dealDetailId
      ? `notes?dealId=${dealId}&dealDetailId=${dealDetailId}&includeDealLevel=true`
      : `notes?dealId=${dealId}&includeDealLevel=true`;
    const tasksUrl = `tasks?dealId=${dealId}` + (dealDetailId ? `&dealDetailId=${dealDetailId}` : "");
    const [notes, tasks] = await Promise.all([api.get(notesUrl), api.get(tasksUrl)]);
    const items = [];
    for (const n of (notes || [])) {
      items.push({ id: `n:${n.noteID}`, type: "note", date: n.noteDate || n.createdUTC, title: noteToText(n.content), by: n.createdByDisplayName });
    }
    for (const t of (tasks || [])) {
      const date = t.isCompleted ? (t.completedDate || t.dueDate) : t.dueDate;
      items.push({ id: `t:${t.taskID}`, type: "task", date, title: t.title, completed: t.isCompleted, status: t.status });
    }
    items.sort((a, b) => (b.date ? new Date(b.date).getTime() : 0) - (a.date ? new Date(a.date).getTime() : 0));
    return items;
  }, [dealId, dealDetailId]);

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} />;
  if (!data || data.length === 0)
    return <div style={{ padding: "30px 0", textAlign: "center", color: B.textLt, fontSize: font.base }}>No notes or completed tasks yet.</div>;

  return (
    <div>
      {data.map(item => (
        <div key={item.id} style={{ display: "flex", gap: 10, padding: "10px 0", borderBottom: `1px solid ${B.divider}`, alignItems: "flex-start" }}>
          <Badge color={item.type === "note" ? B.navy : B.green}>
            {item.type === "note" ? "Note" : (item.completed ? "Task done" : "Task")}
          </Badge>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: font.base, color: B.text, lineHeight: 1.5 }}>
              {item.title || <span style={{ color: B.textLt, fontStyle: "italic" }}>—</span>}
            </div>
            <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 2 }}>
              {fmt.date(item.date)}
              {item.by ? ` · ${item.by}` : ""}
              {item.type === "task" && !item.completed && item.status ? ` · ${item.status}` : ""}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const TaskReport = ({ onDealClick, onTaskClick, user }) => {
  const { data: report, loading, error, reload } = useApi(() => api.get("reports/deal-report"), []);

  const [search,        setSearch]        = useState("");
  const [filterStatus,  setFilterStatus]  = useState("All");
  const [showDead,      setShowDead]      = useState(false);
  // OFF by default: the report is a settled artefact people read weekly, and
  // silently growing it a column would change what everyone sees. Opting in is
  // the "way to add it" — and because it is plain state, it survives into
  // printMode, so a printed report carries the column too.
  const [showIndustry,  setShowIndustry]  = useState(false);
  const [printMode,     setPrintMode]     = useState(false);
  const [expandedDealIds, setExpandedDealIds] = useState(() => new Set());
  const [historyTarget, setHistoryTarget] = useState(null);
  const [editingNaics,  setEditingNaics]  = useState(null);
  // Same local-override layer as the NAICS report: useApi has no setter, and a
  // full reload of the report between edits would be worse than not editing.
  const [naicsEdits,    setNaicsEdits]    = useState({});

  const toggleDeal = (id) => setExpandedDealIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const today0 = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);

  const filtered = useMemo(() => {
    const list = (report || []).map(d => (naicsEdits[d.dealID] ? { ...d, ...naicsEdits[d.dealID] } : d));
    const q = search.trim().toLowerCase();
    return list.filter(d => {
      if (!showDead && d.status === "Dead") return false;
      if (filterStatus !== "All" && d.status !== filterStatus) return false;
      // Only while the column is shown: widening search to a hidden field means
      // a deal matches for a reason nobody can see on screen.
      if (showIndustry && q && (
        String(d.naicsCode || "").includes(q)
        || String(d.naicsDescription || "").toLowerCase().includes(q)
        || String(d.assetTypes || "").toLowerCase().includes(q)
      )) return true;
      if (q) {
        const inName = (d.name || "").toLowerCase().includes(q);
        const inUpd  = (d.latestUpdate?.preview || "").toLowerCase().includes(q);
        const inNext = (d.nextTask?.title || "").toLowerCase().includes(q);
        if (!inName && !inUpd && !inNext) return false;
      }
      return true;
    });
  }, [report, search, filterStatus, showDead, showIndustry, naicsEdits]);

  const summary = useMemo(() => ({
    total:   filtered.length,
    overdue: filtered.filter(d => d.nextTask?.dueDate && new Date(d.nextTask.dueDate) < today0).length,
  }), [filtered, today0]);

  const hasActiveFilters = filterStatus !== "All" || search || showDead;
  const clearFilters = () => { setFilterStatus("All"); setSearch(""); setShowDead(false); };

  const COL = {
    deal:    { flex: "1 1 220px", minWidth: 200 },
    update:  { flex: "1 1 200px", minWidth: 160 },
    next:    { flex: "1 1 200px", minWidth: 160 },
    person:  { flex: "0 0 140px", width: 140 },
    due:     { flex: "0 0 110px", width: 110 },
    industry:{ flex: "0 0 190px", width: 190 },
  };

  // The four data columns (Latest Update → Due Date) for either a deal row or a
  // deal-detail row. `scope` carries { latestUpdate, nextTask }; Person in Charge
  // always shows the deal-level lead names per product decision.
  const renderScopeCells = (scope, leadNames, onHistory) => {
    const lu = scope?.latestUpdate;
    const nt = scope?.nextTask;
    const overdue = nt?.dueDate && new Date(nt.dueDate) < today0;
    return (
      <>
        <div style={COL.update}>
          {lu ? (
            <div {...clickable(onHistory)} title="View full history" style={{ cursor: "pointer" }}>
              <div style={{ fontSize: font.sm, color: B.text, lineHeight: 1.5 }}>{lu.preview || "—"}</div>
              <div style={{ fontSize: font.xs, color: B.navy, marginTop: 3, fontWeight: weight.medium, display: "inline-flex", alignItems: "center", gap: 3 }}>
                {fmt.date(lu.date)} · {lu.kind === "task" ? "Task completed" : "Note"} · History
              </div>
            </div>
          ) : <span style={{ color: B.textLt, fontStyle: "italic", fontSize: font.sm }}>—</span>}
        </div>

        <div style={COL.next}>
          {nt?.title ? (
            <div style={{ fontSize: font.sm, lineHeight: 1.5 }}>
              {(() => {
                const linkable = !!(nt.taskID && onTaskClick);
                return (
                  <span
                    onClick={linkable ? (e) => { e.stopPropagation(); onTaskClick(nt.taskID); } : undefined}
                    title={linkable ? "Open task" : undefined}
                    style={{
                      color: linkable ? B.navy : B.text,
                      cursor: linkable ? "pointer" : "default",
                      fontWeight: linkable ? weight.medium : weight.regular,
                      textDecoration: linkable ? "underline" : "none", textUnderlineOffset: 2,
                    }}>
                    {nt.isPayment ? `Next Loan Payment Due - ${fmtMDY(nt.dueDate)}` : nt.title}
                  </span>
                );
              })()}
              {INLINE_STATUSES.includes(nt.status) && (
                <span style={{ color: B.navyLt, fontWeight: weight.semibold }}> — {nt.status}</span>
              )}
            </div>
          ) : <span style={{ color: B.textLt, fontStyle: "italic", fontSize: font.sm }}>—</span>}
          {nt?.assigneeNames && (
            <div style={{ display: "flex", gap: 3, marginTop: 4, flexWrap: "wrap" }}>
              {nt.assigneeNames.split(", ").map(n => n.trim()).filter(Boolean).map(name => (
                <span key={name} title={name}><Avatar name={name} initials={fmt.initials(name)} size={20} /></span>
              ))}
            </div>
          )}
        </div>

        <div style={COL.person}><LeadAvatars names={leadNames} /></div>

        <div style={COL.due}>
          {nt?.dueDate ? (
            <span style={{ fontSize: font.sm, color: overdue ? B.red : B.text, fontWeight: overdue ? weight.bold : weight.regular, display: "inline-flex", alignItems: "center", gap: 4 }}>
              {overdue && <Icon name="warning" size={13} />}{fmtMDY(nt.dueDate)}
            </span>
          ) : <span style={{ color: B.textLt, fontSize: font.sm }}>—</span>}
        </div>
      </>
    );
  };

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} onRetry={reload} />;

  const headerCells = [
    [COL.deal,   "Deal"],
    // Next to the deal name, not at the far end: the industry is part of what
    // the deal IS, and four columns of activity between them would read as
    // though it were another activity field.
    ...(showIndustry ? [[COL.industry, "Industry (NAICS)"]] : []),
    [COL.update, "Latest Update"],
    [COL.next,   "Next Steps"],
    [COL.person, "Person in Charge"],
    [COL.due,    "Due Date"],
  ];

  // One cell, so the deal row and the detail row cannot drift out of alignment.
  // A detail gets an EMPTY cell rather than the parent's code: a deal detail has
  // no industry of its own, and repeating the parent's would read as though the
  // detail had been classified.
  const industryCell = (deal) => {
    if (!showIndustry) return null;
    const code = deal?.naicsCode;
    const canEdit = user?.role === "Admin";
    const inner = (
      <>
        {code ? (
          <>
            <div style={{ fontSize: font.sm, fontWeight: weight.semibold, color: B.text }}>{code}</div>
            <div style={{ fontSize: font.xs, color: B.textMd, lineHeight: 1.35 }}>
              {deal.naicsDescription || "—"}
            </div>
            {/* Which source set it, because a typed code is a decision and a
                derived one is a reading the sweep will revisit. */}
            {deal.naicsSource === "ai" && (
              <div style={{ fontSize: 10, color: B.textLt, marginTop: 1 }}>from documents</div>
            )}
            {/* What the asset IS, under what the industry is. On a property
                deal the NAICS line is nearly always a lessor code, so this is
                the line that actually distinguishes one from another. */}
            {deal.assetTypes && (
              <div style={{ fontSize: font.xs, color: B.textMd, marginTop: 2, lineHeight: 1.3 }}>
                {deal.assetTypes}
              </div>
            )}
          </>
        ) : (
          <div>
            <div style={{ fontSize: font.xs, color: canEdit ? B.navy : B.textLt }}>
              {canEdit ? "Set…" : "—"}
            </div>
            {/* An unclassified property deal still knows what it holds. */}
            {deal.assetTypes && (
              <div style={{ fontSize: font.xs, color: B.textMd, marginTop: 2, lineHeight: 1.3 }}>
                {deal.assetTypes}
              </div>
            )}
          </div>
        )}
      </>
    );
    return (
      <div key="industry" style={{ ...COL.industry }}>
        {canEdit && !printMode ? (
          <button onClick={() => setEditingNaics(deal)} title="Set this deal's NAICS code"
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
                     font: "inherit", textAlign: "left", textDecoration: "underline",
                     textUnderlineOffset: 3, textDecorationStyle: "dotted", color: "inherit" }}>
            {inner}
          </button>
        ) : inner}
      </div>
    );
  };

  return (
    <div>
      <PageHeader
        icon="taskReport"
        title="Deal Report"
        subtitle={
          summary.overdue > 0
            ? `${summary.total} deal${summary.total !== 1 ? "s" : ""} · ${summary.overdue} with overdue next step`
            : `${summary.total} deal${summary.total !== 1 ? "s" : ""}`
        }
        actions={
          <>
            <Btn size="sm" variant="ghost" onClick={reload}><Icon name="refresh" size={14} /> Refresh</Btn>
            <Btn size="sm" variant="secondary" onClick={() => { setPrintMode(true); setTimeout(() => { window.print(); setPrintMode(false); }, 200); }}>
              <Icon name="print" size={14} /> Print
            </Btn>
          </>
        }
      />

      {/* Filters */}
      {!printMode && (
        <Card style={{ marginBottom: space.xl, padding: "14px 20px" }}>
          <div style={{ display: "flex", gap: space.md, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ flex: "1 1 240px", minWidth: 200 }}>
              <label style={{ display: "block", fontSize: font.xs, fontWeight: weight.semibold, color: B.textMd, marginBottom: space.xs, letterSpacing: 0.5, textTransform: "uppercase" }}>Search</label>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search deals…"
                style={{ width: "100%", padding: "8px 11px", border: `1px solid ${B.border}`, borderRadius: radius.md, fontSize: font.base, fontFamily: fonts.sans, boxSizing: "border-box", outline: "none", background: B.surface }} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: font.xs, fontWeight: weight.semibold, color: B.textMd, marginBottom: space.xs, letterSpacing: 0.5, textTransform: "uppercase" }}>Status</label>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                style={{ padding: "8px 11px", border: `1px solid ${B.border}`, borderRadius: radius.md, fontSize: font.base, fontFamily: fonts.sans, background: B.surface, outline: "none" }}>
                <option value="All">All Statuses</option>
                {["Prospect", "Active"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: font.base, color: B.text, paddingBottom: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={showDead} onChange={e => setShowDead(e.target.checked)} style={{ cursor: "pointer" }} />
              Show dead deals
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: font.base, color: B.text, paddingBottom: 8, cursor: "pointer" }}
                   title="The NAICS industry each deal operates in — typed on the deal, or derived from its documents">
              <input type="checkbox" checked={showIndustry} onChange={e => setShowIndustry(e.target.checked)} style={{ cursor: "pointer" }} />
              Show industry
            </label>
            {hasActiveFilters && (
              <Btn size="sm" variant="ghost" onClick={clearFilters}><Icon name="close" size={13} /> Clear all</Btn>
            )}
          </div>
        </Card>
      )}

      {/* Column header row */}
      <div style={{
        display: "flex", background: B.surfaceMuted, border: `1px solid ${B.border}`,
        borderTopLeftRadius: radius.lg, borderTopRightRadius: radius.lg,
        padding: "11px 16px", gap: 8, position: "sticky", top: 58, zIndex: 10,
      }}>
        {headerCells.map(([s, label]) => (
          <div key={label} style={{ ...s, fontSize: font.xs, fontWeight: weight.semibold, color: B.textMd, letterSpacing: 0.5, textTransform: "uppercase" }}>
            {label}
          </div>
        ))}
      </div>

      {/* Rows container */}
      <div style={{ border: `1px solid ${B.border}`, borderTop: "none", borderBottomLeftRadius: radius.lg, borderBottomRightRadius: radius.lg, background: B.surface, overflow: "hidden" }}>
        {/* Empty state */}
        {filtered.length === 0 && (
          <div style={{ padding: "44px 0", textAlign: "center", color: B.textLt, fontSize: font.md }}>
            No deals match the current filters.
          </div>
        )}

        {/* Deal rows */}
        {filtered.map(deal => {
          const expandable = (deal.detailCount || 0) > 1;
          const expanded   = expandable && expandedDealIds.has(deal.dealID);
          return (
            <div key={deal.dealID}>
              {/* Deal row */}
              <div style={{ display: "flex", gap: 8, padding: "12px 16px", background: B.surface, borderBottom: `1px solid ${B.divider}`, alignItems: "flex-start", transition: "background 0.1s" }}
                onMouseEnter={e => e.currentTarget.style.background = B.hover}
                onMouseLeave={e => e.currentTarget.style.background = B.surface}>
                <div style={COL.deal}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                    <span
                      onClick={() => expandable && toggleDeal(deal.dealID)}
                      style={{
                        width: 14, flexShrink: 0, color: B.textMd, marginTop: 1,
                        cursor: expandable ? "pointer" : "default",
                        display: "inline-flex", transition: "transform 0.15s",
                        transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
                        opacity: expandable ? 1 : 0,
                      }}><Icon name="chevronDown" size={14} /></span>
                    <div
                      onClick={() => onDealClick && onDealClick(deal.dealID)}
                      title={onDealClick ? "Open deal" : undefined}
                      style={{
                        fontSize: font.base, fontWeight: weight.bold, color: B.navy, lineHeight: 1.4,
                        cursor: onDealClick ? "pointer" : "default",
                        textDecoration: onDealClick ? "underline" : "none", textUnderlineOffset: 3,
                      }}>
                      {deal.name}
                    </div>
                  </div>
                  {expandable && (
                    <div {...clickable(() => toggleDeal(deal.dealID))} style={{ fontSize: font.xs, color: B.textLt, marginTop: 3, marginLeft: 20, cursor: "pointer" }}>
                      {deal.detailCount} deal details · {expanded ? "hide" : "show"}
                    </div>
                  )}
                </div>
                {industryCell(deal)}
                {renderScopeCells(deal, deal.harvusLeadNames,
                  () => setHistoryTarget({ dealId: deal.dealID, dealDetailId: null, title: deal.name }))}
              </div>

              {/* Deal-detail child rows */}
              {expanded && (deal.details || []).map(det => (
                <div key={det.dealDetailID} style={{ display: "flex", gap: 8, padding: "10px 16px", background: B.surfaceMuted, borderBottom: `1px solid ${B.divider}`, alignItems: "flex-start" }}>
                  <div style={{ ...COL.deal, paddingLeft: 28 }}>
                    <div style={{ display: "inline-block", padding: "6px 10px", borderRadius: radius.md, background: B.surface, border: `1px solid ${B.border}` }}>
                      <div style={{ fontSize: font.sm, fontWeight: weight.bold, color: B.text }}>{det.label}</div>
                      <div style={{ fontSize: font.xs, color: B.textMd, marginTop: 1 }}>{det.detailType}</div>
                    </div>
                  </div>
                  {showIndustry && <div style={{ ...COL.industry }} />}
                  {renderScopeCells(det, deal.harvusLeadNames,
                    () => setHistoryTarget({ dealId: deal.dealID, dealDetailId: det.dealDetailID, title: `${deal.name} — ${det.label}` }))}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Latest Update history popup */}
      {historyTarget && (
        <Modal title={`History — ${historyTarget.title}`} width={680} onClose={() => setHistoryTarget(null)}>
          <HistoryBody dealId={historyTarget.dealId} dealDetailId={historyTarget.dealDetailId} />
        </Modal>
      )}

      <style>{`
        @media print {
          body { font-size: 11px; }
          nav, button { display: none !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
      {editingNaics && (
        <NaicsEditModal
          deal={editingNaics}
          onClose={() => setEditingNaics(null)}
          onSaved={(row) => setNaicsEdits(prev => ({ ...prev, [row.dealID]: row }))}
        />
      )}
    </div>
  );
};
